import styled from '@emotion/styled';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ChevronSmallDown,
  ChevronSmallUp,
  LoadingIcon,
} from '@ELib/assets/images';
import {mixinCenterWrapper, mixinSVGIcon} from '@ELib/styles/mixins';
import {isAncestorElementOf} from '@ELib/utilities/dom';

import {AnimationWrapper} from '../AnimationWrapper';
import {NETransparentIconButton} from '../EButtons';

import {findTableRow} from './utilities';

/**
 * An entirely empirical value to make sure loading triggers when scrolling up
 * directly after navigating to a specific row.
 *
 * The key is to convince IntersectionObserver that the load trigger is not fully
 * visible. Otherwise, no more events are fired when the user scrolls up.
 */
export const LOAD_TRIGGER_HEIGHT_IN_REM = 4;

/** How long a resolved load may still wait for the rows it loaded. */
const LOAD_FALLBACK_DELAY_IN_MS = 500;
const LOAD_TRIGGER_HEIGHT_REM = `${LOAD_TRIGGER_HEIGHT_IN_REM}rem`;

/**
 * Component that is responsible for showing a loading indicator
 * and lazily-loading new data on scroll. Note that there are
 * different paging mechanisms. More information can be found at
 * https://lexian-ag.atlassian.net/wiki/spaces/LEXDEV/pages/1002231/Pagination+Mechanics
 */
export function LoadTrigger({
  isTop = false,
  tableRef,
  scrollElementRef,
  isSelectable,
  hasExpansion,
  columnsLength,
  onLoad,
  onLoadFinished,
  numRows,
  skipScrollAnchoring = false,
}: {
  /** If set to true, this component is expected to be the top (not bottom) load trigger. */
  isTop?: boolean;
  tableRef: RefObject<HTMLTableElement | null>;
  /** The scrollable element that wraps the table. */
  scrollElementRef: RefObject<HTMLDivElement | null>;
  /** Flag whether the table has a selection column. */
  isSelectable: boolean;
  /** Flag whether the table has an expansion column. */
  hasExpansion: boolean;
  /** Number of table columns. */
  columnsLength: number;
  /** Custom loading handler. *Must* only resolve when the new data has finished loading. */
  onLoad: () => Promise<void>;
  onLoadFinished?: () => void;
  numRows: number;
  /**
   * Whether the scroll position anchoring after a load is handled by the
   * caller instead of this component (e.g. by the virtualizer).
   */
  skipScrollAnchoring?: boolean;
}): React.ReactElement {
  // Set the initial scroll position so that the load trigger is not visible initially.
  const tableParentRef = scrollElementRef.current;
  const loadTriggerRef = useRef<HTMLTableRowElement>(null);
  const [isInitialScrollingDone, setIsInitialScrollingDone] = useState(false);
  useEffect(() => {
    if (isTop && !isInitialScrollingDone && tableParentRef) {
      tableParentRef.scrollTop = loadTriggerRef.current?.scrollHeight ?? 0;
      setIsInitialScrollingDone(true);
    }
  }, [isInitialScrollingDone, isTop, tableParentRef]);

  // declare intersection callback function
  const [isLoading, setIsLoading] = useState(false);
  const [scrollingInfo, setScrollingInfo] = useState<{
    /** Anchor row id; not set when only waiting for the load to render. */
    id?: string;
    position?: number;
    numRowsOnLoad: number;
    /** Whether the load itself has already resolved. */
    isLoadResolved?: boolean;
  }>();

  const markLoadResolved = useCallback(() => {
    setScrollingInfo((info) =>
      info === undefined ? info : {...info, isLoadResolved: true}
    );
  }, []);

  const loadFallbackRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  /**
   * A load that brings no new rows (an empty page while there is still more to
   * come) never reaches the effect below through a changed row count, and the
   * trigger would keep spinning. The resolved load alone is no proof either:
   * the render with the new rows can land later than the promise. So wait a
   * moment for them and only then give up on them.
   */
  const armLoadFallback = useCallback(() => {
    clearTimeout(loadFallbackRef.current);
    loadFallbackRef.current = setTimeout(
      markLoadResolved,
      LOAD_FALLBACK_DELAY_IN_MS
    );
  }, [markLoadResolved]);

  useEffect(() => () => clearTimeout(loadFallbackRef.current), []);

  const doLoad = useCallback(async () => {
    setIsLoading(true);

    if (skipScrollAnchoring) {
      // the virtualizer keeps the scroll position itself, but the load only
      // counts as finished once the grown data window has rendered (see the
      // effect below) — onLoadFinished may replay a buffered key event
      // against the rows
      setScrollingInfo({numRowsOnLoad: numRows});
      await onLoad();
      armLoadFallback();
      return;
    }

    /**
     * Element that is used as a scroll anchoring reference. If the top LoadTrigger
     * is visible, this will be the first data row. If the bottom LoadTrigger is
     * visible, this will be the last data row.
     * On triggering a load, the scroll position of the scroll anchor element is
     * kept in memory. After the data has finished loading, the scroll position is
     * set according to the *new* position of the scroll anchor element.
     */
    const dataRows = loadTriggerRef.current
      ?.closest('tbody')
      ?.querySelectorAll(':scope > tr[data-id]');
    const scrollAnchorRow = isTop
      ? dataRows?.[0]
      : dataRows?.[dataRows.length - 1];
    const scrollAnchorId = scrollAnchorRow?.getAttribute('data-id');
    const scrollAnchorPosition = scrollAnchorRow?.getBoundingClientRect().top;

    if (!scrollAnchorId || scrollAnchorPosition === undefined) {
      // no row to keep stable (e.g. the table is still empty): plain load
      // without scroll anchoring
      await onLoad();
      onLoadFinished?.();
      setIsLoading(false);
      return;
    }

    setScrollingInfo({
      id: scrollAnchorId,
      position: scrollAnchorPosition,
      numRowsOnLoad: numRows,
    });

    await onLoad();
    armLoadFallback();
  }, [
    isTop,
    onLoad,
    onLoadFinished,
    numRows,
    skipScrollAnchoring,
    armLoadFallback,
  ]);

  useEffect(() => {
    if (scrollingInfo === undefined) {
      return;
    }
    const hasNewRows = scrollingInfo.numRowsOnLoad !== numRows;
    if (!hasNewRows && !scrollingInfo.isLoadResolved) {
      return;
    }

    // set the element again as re-rendering the row list removes the previous element from the DOM
    const scrollAnchorRow =
      scrollingInfo.id === undefined
        ? undefined
        : findTableRow(tableRef.current, scrollingInfo.id);
    // The anchor may be unusable now: the row left the DOM (window trimmed),
    // or the data crossed the virtualization threshold, so the virtualizer
    // keeps the scroll stable itself and anchoring would double-compensate.
    // Without new rows nothing moved, so there is nothing to anchor either.
    if (
      hasNewRows &&
      !skipScrollAnchoring &&
      scrollAnchorRow &&
      tableParentRef &&
      scrollingInfo.position !== undefined
    ) {
      const currentPosition = scrollAnchorRow.getBoundingClientRect().top;
      const scrollBy = currentPosition - scrollingInfo.position;
      tableParentRef.scrollBy({top: scrollBy});
    }
    onLoadFinished?.();

    clearTimeout(loadFallbackRef.current);
    setScrollingInfo(undefined);
    setIsLoading(false);
  }, [
    onLoadFinished,
    scrollingInfo,
    skipScrollAnchoring,
    tableParentRef,
    tableRef,
    numRows,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const intersectionCallback: IntersectionObserverCallback = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      const {boundingClientRect, isIntersecting} = entries[0]!;

      if (!isIntersecting || isLoading) {
        return;
      }

      // The top load trigger can be covered by the sticky header row.
      // It is therefore necessary to check whether the load trigger
      // is actually visible to the user already. Only when it is
      // actually visible, the lazy-loading should start.
      if (
        isTop &&
        !isTriggerVisible(loadTriggerRef.current, boundingClientRect)
      ) {
        return;
      }

      await doLoad();
    },
    [doLoad, isLoading, isTop]
  );

  // set up intersection observer to detect visible load trigger
  useEffect(() => {
    const loadTriggerEl = loadTriggerRef.current;
    if (!loadTriggerEl) {
      return;
    }
    const observer = new IntersectionObserver(intersectionCallback, {
      root: tableParentRef,
      // Having multiple thresholds is necessary as the top load trigger may be
      // covered by the table header row at the first intersection. So further
      // intersection events will be required to be able to properly detect
      // the appearance of the load trigger.
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    });
    observer.observe(loadTriggerEl);
    return () => {
      observer.unobserve(loadTriggerEl);
    };
  }, [intersectionCallback, tableParentRef]);

  const colSpan = columnsLength - Number(isSelectable) - Number(hasExpansion);
  return (
    <tr
      ref={loadTriggerRef}
      className='noHover'
      style={{height: LOAD_TRIGGER_HEIGHT_REM}}
    >
      {isSelectable && <td className='selection' />}
      {hasExpansion && <td />}
      <td colSpan={colSpan}>
        <CenteringWrapper>
          {isLoading ? (
            <AnimationWrapper>
              <LoadingIcon />
            </AnimationWrapper>
          ) : isTop ? (
            <NETransparentIconButton
              onClick={() => {
                doLoad();
              }}
            >
              <ChevronSmallUp />
            </NETransparentIconButton>
          ) : (
            <NETransparentIconButton
              onClick={() => {
                doLoad();
              }}
            >
              <ChevronSmallDown />
            </NETransparentIconButton>
          )}
        </CenteringWrapper>
      </td>
    </tr>
  );
}

const TRIGGER_IS_VISIBLE_X_STEP_SIZE = 100;

function isTriggerVisible(
  loadTrigger: HTMLTableRowElement | null,
  boundingClientRect: DOMRectReadOnly
): boolean {
  if (!loadTrigger) {
    return false;
  }

  let xPosition = boundingClientRect.left + TRIGGER_IS_VISIBLE_X_STEP_SIZE;

  while (xPosition < boundingClientRect.right) {
    /** Top-most visible element given the coordinates. */
    const elementAtBoundingRect = document.elementFromPoint(
      xPosition,
      // decrementing seems necessary, otherwise the wrong element is selected
      boundingClientRect.bottom - 1
    );

    const isVisible = isAncestorElementOf(loadTrigger, elementAtBoundingRect);

    if (isVisible) {
      return true;
    }

    xPosition += TRIGGER_IS_VISIBLE_X_STEP_SIZE;
  }

  return false;
}

const CenteringWrapper = styled.div`
  ${mixinCenterWrapper}
  && {
    opacity: 1;
  }

  svg {
    color: var(--core-core-grey);
    ${mixinSVGIcon}
  }
`;
